import React from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { adminApi } from '../../../lib/adminAuth';
import ListEditor from './ListEditor';
import { IconBtn } from './ContractsPanel';
import { errMsg } from './docUtils';

function ServiceDialog({ item, onClose, onSaved }) {
  const [f, setF] = React.useState(null);
  React.useEffect(() => { setF(item ? { area: '', included: '', deliverables: [], default_on: true, ...item } : null); }, [item]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { area: f.area, included: f.included, deliverables: f.deliverables, default_on: f.default_on };
      if (f._id) await adminApi.put(`/api/admin/scope-library/services/${f._id}`, body);
      else await adminApi.post('/api/admin/scope-library/services', body);
      toast.success('Service saved'); onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="library-service-dialog">
        <DialogHeader><DialogTitle>{f._id ? 'Edit service' : 'New service'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} placeholder="Area, e.g. Performance marketing" data-testid="library-service-area" />
          <Textarea rows={3} value={f.included} onChange={(e) => setF({ ...f, included: e.target.value })} placeholder="What is included" data-testid="library-service-included" />
          <p className="text-xs font-semibold text-slate-600">Deliverables</p>
          <ListEditor value={f.deliverables} rows={1} onChange={(d) => setF({ ...f, deliverables: d })} addLabel="Add deliverable" testId="library-service-deliverables" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.default_on} onCheckedChange={(v) => setF({ ...f, default_on: v })} data-testid="library-service-default" /> Ticked by default on new documents</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.area.trim()} className="bg-gray-900 hover:bg-gray-800" data-testid="library-service-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LibraryServices({ services, canEdit, reload }) {
  const [editing, setEditing] = React.useState(null);
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.area}" from the library? Existing documents keep their copy.`)) return;
    try { await adminApi.delete(`/api/admin/scope-library/services/${s._id}`); toast.success('Service deleted'); reload(); }
    catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };
  return (
    <div className="space-y-2" data-testid="library-services">
      {canEdit && <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="library-service-new"><Plus className="w-4 h-4 mr-1" /> Add service</Button>}
      {services.map((s) => (
        <div key={s._id} className="flex gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid={`library-service-${s._id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">{s.area} {s.default_on ? <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">default</span> : <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">optional</span>}</p>
            <p className="text-xs text-slate-500">{s.included}</p>
            {s.deliverables?.length > 0 && <ul className="mt-1 text-xs text-slate-500 list-disc pl-4">{s.deliverables.map((d, i) => <li key={i}>{d}</li>)}</ul>}
          </div>
          {canEdit && <div className="flex gap-0.5 shrink-0">
            <IconBtn title="Edit" onClick={() => setEditing(s)} testId={`library-service-edit-${s._id}`}><Pencil /></IconBtn>
            <IconBtn title="Delete" danger onClick={() => remove(s)} testId={`library-service-delete-${s._id}`}><Trash2 /></IconBtn>
          </div>}
        </div>
      ))}
      <ServiceDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
    </div>
  );
}
